import type { AddonManagementView } from 'components/AddonManagement/AddonManagementViewSelector';
import type { ApiErrorMessage } from 'epic/apiErrorEpicHelper';
import type { AddonManagementQuoteResultWithTradeWindowDetails } from 'epic/postTradeOperations/addonManagement/pricingConflationEpics';
import type { IntlKey } from 'locales';
import type { FormError } from 'models/formError';
import type { AddonManagementFormResult } from 'models/forms/AddonManagementFormResult';
import type { NextWorkflowError } from 'models/NextWorkflowError';
import type { QuoteAck } from 'models/quote/QuoteAck';
import type { QuoteAction } from 'state/common-form/infos/infos.model';
import type { FieldMetadataById } from 'state/common-form/metaData/fields/fields.model';
import type { DataById } from 'state/formData/formData.model';

import * as actionTypes from './addonManagementForm.actionTypes';

export type AddonManagementFormActions =
  | AddonFormsRequested
  | AddonFormsReceived
  | AddonFormsFailed
  | AddonFormsTimeouted
  | FilterAction
  | FormAction
  | QuoteActions
  | TradeActions;

type FilterAction = AddonManagementFormViewSelected;

type FormAction =
  | AddonManagementFormSelectToggled
  | AddonDefaultingToggled
  | AddonManagementFormEnrichmentReceived
  | AddonManagementFormEnrichmentFailed
  | AddonManagementFormHeaderCheckboxToggle
  | AddonManagementFormUncheckAllCheckbox
  | AddonManagementFormDataChangedWithoutPatch
  | AddonTabCreatedFromAddonManagement;

type QuoteActions =
  | AddonManagementQuoteAddToQueue
  | AddonManagementQuoteAcked
  | AddonManagementQuoteCanceled
  | AddonManagementQuoteRequestFailed
  | AddonManagementQuoteResultFailed
  | AddonManagementQuoteRemoved
  | AddonManagementQuoteReceived
  | AddonManagementQuoteRequested
  | AddonManagementQuoteAborted
  | AddonManagementQuotePushBackCancelRequested
  | AddonManagementQuoteExpired;

type TradeActions =
  | AddonManagementQuoteTradeRequested
  | AddonManagementQuoteTradeSent
  | AddonManagementQuoteTradeFailed
  | AddonManagementQuoteTradeRequestRejected
  | AddonManagementQuoteTradeDone
  | AddonManagementQuoteTradeAcked
  | AddonManagementQuoteTradeRejected;

export function addonFormsRequested() {
  return {
    type: actionTypes.ADDON_MANAGEMENT_FORMS_REQUESTED,
  };
}
export type AddonFormsRequested = ReturnType<typeof addonFormsRequested>;

export function addonFormsReceived(forms: readonly AddonManagementFormResult[], isTradesFetchSuccessful: boolean) {
  return {
    type: actionTypes.ADDON_MANAGEMENT_FORMS_RECEIVED,
    forms,
    isTradesFetchSuccessful,
  };
}
export type AddonFormsReceived = ReturnType<typeof addonFormsReceived>;

export function addonFormsFailed(error: Error) {
  return { type: actionTypes.ADDON_MANAGEMENT_FORMS_FAILED, error };
}
export type AddonFormsFailed = ReturnType<typeof addonFormsFailed>;

export function addonFormsTimeouted() {
  return { type: actionTypes.ADDON_MANAGEMENT_FORMS_TIMEOUTED };
}
export type AddonFormsTimeouted = ReturnType<typeof addonFormsTimeouted>;

////////////////////////////

export function addonManagementFormSelectToggled(formId: string) {
  return { type: actionTypes.ADDON_MANAGEMENT_FORM_SELECT_TOGGLED, formId };
}
export type AddonManagementFormSelectToggled = ReturnType<typeof addonManagementFormSelectToggled>;

export function addonDefaultingToggled(formId: string) {
  return { type: actionTypes.ADDON_DEFAULTING_TOGGLED, formId };
}
export type AddonDefaultingToggled = ReturnType<typeof addonDefaultingToggled>;

export function addonManagementFormViewSelected(view: AddonManagementView) {
  return { type: actionTypes.ADDON_MANAGEMENT_FORM_VIEW_SELECTED, view };
}
export type AddonManagementFormViewSelected = ReturnType<typeof addonManagementFormViewSelected>;

export function addonManagementFormEnrichmentReceived(
  data: DataById,
  fieldsMetadata: FieldMetadataById,
  formId: string,
  actions: readonly QuoteAction[],
  validationErrors?: readonly FormError[],
) {
  return {
    type: actionTypes.ADDON_MANAGEMENT_FORM_ENRICHMENT_RECEIVED,
    data,
    fieldsMetadata,
    formId,
    actions,
    validationErrors,
  };
}
export type AddonManagementFormEnrichmentReceived = ReturnType<typeof addonManagementFormEnrichmentReceived>;

export function addonManagementFormEnrichmentFailed(formId: string, pendingData: DataById) {
  return {
    type: actionTypes.ADDON_MANAGEMENT_FORM_ENRICHMENT_FAILED,
    formId,
    pendingData,
  };
}
export type AddonManagementFormEnrichmentFailed = ReturnType<typeof addonManagementFormEnrichmentFailed>;

export function addonManagementFormHeaderCheckboxToggle() {
  return {
    type: 'ADDON_MANAGEMENT_FORM_HEADER_CHECKBOX_TOGGLE' as const,
  };
}
export type AddonManagementFormHeaderCheckboxToggle = ReturnType<typeof addonManagementFormHeaderCheckboxToggle>;

export function addonManagementFormUncheckAllCheckbox() {
  return {
    type: 'ADDON_MANAGEMENT_FORM_UNCHECK_ALL_CHECKBOX' as const,
  };
}

export type AddonManagementFormUncheckAllCheckbox = ReturnType<typeof addonManagementFormUncheckAllCheckbox>;

export function addonManagementQuoteAddToQueue(formIdsToQuoteIds: Record<string, string>) {
  return {
    type: actionTypes.ADDON_MANAGEMENT_QUOTE_ADD_TO_QUEUE,
    formIdsToQuoteIds,
  };
}

export type AddonManagementQuoteAddToQueue = ReturnType<typeof addonManagementQuoteAddToQueue>;

export function addonManagementQuoteRequested(formIds: readonly string[]) {
  return {
    type: actionTypes.ADDON_MANAGEMENT_QUOTE_REQUESTED,
    formIds,
  };
}

export type AddonManagementQuoteRequested = ReturnType<typeof addonManagementQuoteRequested>;

export const addonManagementQuoteAborted = (formId: string, quoteId?: string) => ({
  type: actionTypes.ADDON_MANAGEMENT_QUOTE_ABORTED,
  formId,
  quoteId,
});
export type AddonManagementQuoteAborted = ReturnType<typeof addonManagementQuoteAborted>;

export function addonManagementQuoteCancelled(productId: string) {
  return {
    type: actionTypes.ADDON_MANAGEMENT_QUOTE_CANCELLED,
    productId,
  };
}
export type AddonManagementQuoteCanceled = ReturnType<typeof addonManagementQuoteCancelled>;

export function addonManagementQuotePushBackCancelRequested(formId: string) {
  return { type: actionTypes.ADDON_MANAGEMENT_QUOTE_PUSH_BACK_CANCEL_REQUESTED, formId };
}
export type AddonManagementQuotePushBackCancelRequested = ReturnType<
  typeof addonManagementQuotePushBackCancelRequested
>;

export function addonManagementQuoteAcked(forms: readonly QuoteAck[]) {
  return {
    type: actionTypes.ADDON_MANAGEMENT_QUOTE_ACKED,
    forms,
  };
}
export type AddonManagementQuoteAcked = ReturnType<typeof addonManagementQuoteAcked>;

export function addonManagementQuoteRemoved(formIds: readonly string[]) {
  return {
    type: actionTypes.ADDON_MANAGEMENT_QUOTE_REMOVED,
    formIds,
  };
}

export type AddonManagementQuoteRemoved = ReturnType<typeof addonManagementQuoteRemoved>;

export function addonManagementQuoteReceived(
  formId: string,
  quoteId: string,
  quote: AddonManagementQuoteResultWithTradeWindowDetails,
  timeToTradeInMs?: number,
) {
  return {
    type: actionTypes.ADDON_MANAGEMENT_QUOTE_RECEIVED,
    formId,
    quoteId,
    timeToTradeInMs,
    quote,
  };
}
export type AddonManagementQuoteReceived = ReturnType<typeof addonManagementQuoteReceived>;

export function addonManagementQuoteResultFailed(formId: string, quoteId: string, technicalError: ApiErrorMessage) {
  return {
    type: actionTypes.ADDON_MANAGEMENT_QUOTE_RESULT_FAILED,
    formId,
    quoteId,
    technicalError,
  };
}

export type AddonManagementQuoteResultFailed = ReturnType<typeof addonManagementQuoteResultFailed>;

export function addonManagementQuoteRequestFailed(forms: ReadonlyArray<{ formId: string }>) {
  return {
    type: actionTypes.ADDON_MANAGEMENT_QUOTE_REQUEST_FAILED,
    forms,
  };
}

export type AddonManagementQuoteRequestFailed = ReturnType<typeof addonManagementQuoteRequestFailed>;

export function addonManagementQuoteTradeRequested(formId: string, quoteId: string) {
  return {
    type: actionTypes.ADDON_MANAGEMENT_QUOTE_TRADE_REQUESTED,
    formId,
    quoteId,
  };
}

export type AddonManagementQuoteTradeRequested = ReturnType<typeof addonManagementQuoteTradeRequested>;

export function addonManagementQuoteTradeAcked(formId: string, quoteId: string) {
  return {
    type: actionTypes.ADDON_MANAGEMENT_QUOTE_TRADE_ACKED,
    formId,
    quoteId,
  };
}
export type AddonManagementQuoteTradeAcked = ReturnType<typeof addonManagementQuoteTradeAcked>;
export function addonManagementQuoteTradeSent(formId: string, quoteId: string) {
  return {
    type: actionTypes.ADDON_MANAGEMENT_QUOTE_TRADE_SENT,
    formId,
    quoteId,
  };
}

export type AddonManagementQuoteTradeSent = ReturnType<typeof addonManagementQuoteTradeSent>;

export function addonManagementQuoteTradeFailed(formId: string, quoteId: string, error: ApiErrorMessage, cause: any) {
  return {
    type: actionTypes.ADDON_MANAGEMENT_QUOTE_TRADE_FAILED,
    formId,
    quoteId,
    error,
    cause,
  };
}

export type AddonManagementQuoteTradeFailed = ReturnType<typeof addonManagementQuoteTradeFailed>;

export function addonManagementQuoteTradeRequestRejected(formId: string, quoteId: string, error: IntlKey) {
  return {
    type: actionTypes.ADDON_MANAGEMENT_QUOTE_TRADE_REQUEST_REJECTED,
    formId,
    quoteId,
    error,
  };
}
export type AddonManagementQuoteTradeRequestRejected = ReturnType<typeof addonManagementQuoteTradeRequestRejected>;

export function addonManagementQuoteTradeDone(
  formId: string,
  quoteId: string,
  status: string,
  execMessages?: readonly string[],
) {
  return {
    type: actionTypes.ADDON_MANAGEMENT_QUOTE_TRADE_DONE,
    formId,
    quoteId,
    status,
    execMessages,
  };
}
export type AddonManagementQuoteTradeDone = ReturnType<typeof addonManagementQuoteTradeDone>;

export function addonManagementQuoteTradeRejected(
  formId: string,
  quoteId: string,
  rejections: readonly NextWorkflowError[],
) {
  return {
    type: actionTypes.ADDON_MANAGEMENT_QUOTE_TRADE_REJECTED,
    formId,
    quoteId,
    rejections,
  };
}
export type AddonManagementQuoteTradeRejected = ReturnType<typeof addonManagementQuoteTradeRejected>;

export function addonManagementQuoteExpired(formId: string, quoteId: string) {
  return {
    type: actionTypes.ADDON_MANAGEMENT_QUOTE_EXPIRED,
    formId,
    quoteId,
  };
}
export type AddonManagementQuoteExpired = ReturnType<typeof addonManagementQuoteExpired>;

export function addonManagementFormDataChangedWithoutPatch(formId: string, key: string, value: Readonly<unknown>) {
  return {
    type: actionTypes.ADDON_MANAGEMENT_FORM_DATA_CHANGED_WITHOUT_PATCH,
    formId,
    key,
    value,
  };
}
export type AddonManagementFormDataChangedWithoutPatch = ReturnType<typeof addonManagementFormDataChangedWithoutPatch>;

export function addonTabCreatedFromAddonManagement(tabId: string, productId: string, rfqId: string, tabName: string) {
  return {
    type: actionTypes.ADDON_TAB_CREATED_FROM_ADDON_MANAGEMENT,
    tabId,
    productId,
    rfqId,
    tabName,
  };
}
export type AddonTabCreatedFromAddonManagement = ReturnType<typeof addonTabCreatedFromAddonManagement>;
